import { z } from 'zod';
import { PermissionKeySchema, TenantRoleSchema } from './permissions';
import {
  IssueTypePortalSchema,
  PortalCategorySchema,
  ProjectKeySchema,
  TerminologySchema,
} from './projects';
import { AppUserSchema, OrganizationSchema, TenantBrandingSchema } from './tenancy';

/*
 * Bolt 7 (ADR-0018): read-only metadata the requester portal renders itself from. Everything
 * here is a projection of configuration the admin API owns; nothing is writable through `/v1`.
 */

/** `GET /v1/projects` item: a project the caller may view (`project.view`) or raise requests in. */
export const PortalProjectSchema = z.object({
  id: z.uuid(),
  key: ProjectKeySchema,
  name: z.string(),
  category: z.string().nullable(),
  settings: z.object({
    /** Word for work items in this project's UI (`issue` | `request` | `task`). */
    terminology: TerminologySchema,
    /** Help-center groups; `IssueTypePortal.group` refers to a `key` here. */
    portalCategories: z.array(PortalCategorySchema),
  }),
  /** The caller holds `ticket.create` in the project. */
  canCreate: z.boolean(),
});
export type PortalProject = z.infer<typeof PortalProjectSchema>;

/**
 * `GET /v1/projects/{id}/request-types` item: a portal-visible, non-subtask issue type with the
 * id of the `portal` form to render (type-specific first, else the project-wide one, else null —
 * the create endpoint then applies the project's bindings without a layout).
 */
export const PortalRequestTypeSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  position: z.int().min(0),
  portal: IssueTypePortalSchema,
  formId: z.uuid().nullable(),
});
export type PortalRequestType = z.infer<typeof PortalRequestTypeSchema>;

/** `GET /v1/tenant/branding`: what the portal shell needs before anything else renders. */
export const TenantBrandingResponseSchema = z.object({
  name: z.string(),
  settings: z.object({ branding: TenantBrandingSchema }),
});
export type TenantBrandingResponse = z.infer<typeof TenantBrandingResponseSchema>;

/** One visible project of `GET /v1/me` with the caller's effective permission keys (registry order). */
export const PortalMeProjectSchema = z.object({
  id: z.uuid(),
  key: ProjectKeySchema,
  name: z.string(),
  permissions: z.array(PermissionKeySchema),
});
export type PortalMeProject = z.infer<typeof PortalMeProjectSchema>;

/** `GET /v1/me` (portal variant of `/admin/v1/me`): identity, organizations, permission summary. */
export const PortalMeResponseSchema = z.object({
  user: AppUserSchema.pick({
    id: true,
    displayName: true,
    email: true,
    avatarUrl: true,
    timezone: true,
  }),
  tenantRole: TenantRoleSchema,
  organizations: z.array(OrganizationSchema.pick({ id: true, name: true })),
  projects: z.array(PortalMeProjectSchema),
});
export type PortalMeResponse = z.infer<typeof PortalMeResponseSchema>;

/** A workflow status as requesters see it: `portalName` wins over the internal name when set. */
export const PortalStatusSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  name: z.string(),
  portalName: z.string().nullable(),
  category: z.enum(['todo', 'in_progress', 'done']),
  position: z.int(),
});
export type PortalStatus = z.infer<typeof PortalStatusSchema>;

export const PortalStatusListResponseSchema = z.object({ items: z.array(PortalStatusSchema) });
export type PortalStatusListResponse = z.infer<typeof PortalStatusListResponseSchema>;
